import { useEffect, useState } from "react";
import { BookOpen, Compass, History, Home, MessageCircleMore, RefreshCw, Settings } from "lucide-react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Button, Card, LogoSymbol, PageLoader, Wordmark } from "@samklang/ui";
import { useConversationStore } from "@/stores/conversation-store";
import { getOrganizationAccess, OrganizationAccessError } from "@/services/organization-access";
import { initializeBilling, isNativeBillingAvailable } from "@/services/billing";
import { isPublicAppPath } from "@/services/public-route";

const navigation = [
  { to: "/home" as const, label: "Hem", icon: Home },
  { to: "/conversations" as const, label: "Samtal", icon: MessageCircleMore },
  { to: "/kartlaggningar" as const, label: "Kartläggningar", icon: Compass },
  { to: "/library" as const, label: "Bibliotek", icon: BookOpen },
  { to: "/history" as const, label: "Historik", icon: History },
  { to: "/settings" as const, label: "Inställningar", icon: Settings },
];

export function AppLayout() {
  const pathname = useRouterState().location.pathname;
  const navigate = useNavigate();
  const publicPath = isPublicAppPath(pathname);
  const [accessState, setAccessState] = useState<"checking" | "ready" | "unavailable">(publicPath ? "ready" : "checking");
  const checkAccess = async () => {
    if (publicPath) { setAccessState("ready"); return; }
    setAccessState("checking");
    try {
      const access = await getOrganizationAccess();
      if (isNativeBillingAvailable()) await initializeBilling(access.organization.id);
      setAccessState("ready");
    } catch (error) {
      if (error instanceof OrganizationAccessError && error.code === "access_expired") {
        await navigate({ to: "/subscription", replace: true });
        return;
      }
      if (error instanceof OrganizationAccessError && (error.code === "not_authenticated" || error.code === "account_not_activated")) {
        await navigate({ to: "/login", replace: true });
        return;
      }
      setAccessState("unavailable");
    }
  };
  useEffect(() => { void checkAccess(); }, [pathname]);
  const isSession = pathname.startsWith("/sessions/");
  const showNavigation = !isSession && !publicPath;
  const sessionModeLabel = pathname.includes("/professional") || pathname.startsWith("/sessions/new/")
    ? "Professionellt läge"
    : pathname.includes("/summary") || pathname.includes("/actions")
      ? "Gemensam vy"
      : "Barnets vy";
  const textSize = useConversationStore((store) => store.preferences.textSize);
  const textSizeClass = `samklang-text-${textSize}`;

  if (!publicPath && accessState === "checking") return <div className="min-h-dvh"><SkipLink /><CompactHeader alwaysVisible /><main id="main-content"><PageLoader label="Kontrollerar organisationens tillgång" /></main></div>;
  if (!publicPath && accessState === "unavailable") return <div className="min-h-dvh"><SkipLink /><CompactHeader alwaysVisible /><main id="main-content" className="mx-auto max-w-2xl px-5 py-10"><Card padding="lg"><h1 className="font-display text-3xl font-semibold">Tillgången kunde inte kontrolleras</h1><p className="mt-3 text-foreground-muted">Kontrollera anslutningen och försök igen. Samklang öppnar inte sparade organisationsfunktioner utan en bekräftad åtkomstkontroll.</p><Button className="mt-6" onClick={() => void checkAccess()}><RefreshCw size={18} /> Försök igen</Button></Card></main></div>;

  if (!showNavigation) {
    return (
      <div className={`min-h-dvh ${textSizeClass}`}>
        <SkipLink />
        <CompactHeader modeLabel={isSession ? sessionModeLabel : "Prata på ditt sätt"} rootLink alwaysVisible />
        <main id="main-content">
          <Outlet />
        </main>
        {isSession ? null : <AppFooter />}
      </div>
    );
  }

  return (
    <div className={`min-h-dvh md:grid md:grid-cols-[6.25rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)] ${textSizeClass}`}>
      <SkipLink />
      <TabletNavigation />
      <div className="flex min-h-dvh min-w-0 flex-col pb-20 md:pb-0">
        <CompactHeader />
        <main id="main-content" className="min-w-0 flex-1">
          <Outlet />
        </main>
        <AppFooter />
        <MobileNavigation />
      </div>
    </div>
  );
}

function SkipLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-primary focus:px-4 focus:py-3 focus:text-white"
    >
      Hoppa till innehåll
    </a>
  );
}

function CompactHeader({ modeLabel = "Prata på ditt sätt", rootLink = false, alwaysVisible = false }: { readonly modeLabel?: string; readonly rootLink?: boolean; readonly alwaysVisible?: boolean }) {
  return (
    <header className={`mx-auto w-full max-w-7xl items-center justify-between px-5 py-5 sm:px-8 ${alwaysVisible ? "flex" : "flex md:hidden"}`}>
      <Link
        to={rootLink ? "/" : "/home"}
        aria-label="Samklangs startsida"
        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Wordmark />
      </Link>
      <span className="rounded-full bg-surface-raised/80 px-4 py-2 text-sm font-medium text-foreground-muted shadow-sm">
        {modeLabel}
      </span>
    </header>
  );
}

function TabletNavigation() {
  return (
    <aside className="sticky top-0 hidden h-dvh flex-col border-r border-border/70 bg-surface-raised/72 px-3 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-[max(env(safe-area-inset-top),1.5rem)] backdrop-blur-xl md:flex xl:px-5">
      <Link
        to="/home"
        aria-label="Samklangs startsida"
        className="mx-auto rounded-2xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:mx-0"
      >
        <LogoSymbol className="h-11 w-11 xl:hidden" />
        <Wordmark className="hidden xl:block" />
      </Link>
      <nav className="mt-8 flex flex-1 flex-col gap-2" aria-label="Huvudnavigation">
        {navigation.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            activeProps={{ className: "bg-primary-soft text-primary" }}
            className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-2xl px-2 text-center text-[11px] font-semibold leading-tight text-foreground-muted transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:min-h-12 xl:flex-row xl:justify-start xl:gap-3 xl:px-4 xl:text-sm"
          >
            <Icon aria-hidden="true" size={21} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
      <p className="hidden text-xs leading-relaxed text-foreground-subtle xl:block">
        Ett icke-kliniskt samtalsstöd.
      </p>
    </aside>
  );
}

function MobileNavigation() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-border bg-surface-raised/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      aria-label="Huvudnavigation"
    >
      {navigation.map(({ to, label, icon: Icon }) => (
        <Link
          key={to}
          to={to}
          activeProps={{ className: "text-primary" }}
          className="flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold text-foreground-muted"
        >
          <Icon aria-hidden="true" size={21} />
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
}

function AppFooter() {
  return (
    <footer className="mx-auto w-full max-w-7xl px-5 py-8 text-center text-sm text-foreground-muted sm:px-8 lg:px-10">
      Samklang är ett samtalsstöd, inte ett diagnostiskt verktyg.
    </footer>
  );
}
